import i18n from "./i18n.ts";
import { translateAwardName } from "./awardNames.ts";
import { translateInjuryName } from "./injuryNames.ts";
import { ordinalLocalized } from "./ordinalLocalized.ts";
import { pronounLocalized } from "./pronounLocalized.ts";
import {
	translateTeamFullName,
	translateTeamName,
	translateTeamRegion,
} from "./teamNames.ts";
import { GAME_NAME } from "../../../../common/constants.ts";
import { DEFAULT_TRAGIC_DEATH_REASON_TO_KEY } from "../../../../common/tragicDeaths.ts";

// Legacy events persisted before worker started emitting `textKey` are stuck
// with plain English `text`. Map a few high-frequency canonical strings back
// to their i18n keys so users still see localized text in inbox / news feed /
// transactions / player history for those old events.
const LEGACY_TEXT_TO_KEY: Record<string, string> = {
	"Welcome to your new league!": "event.welcomeNewLeague",
	"Phase change already in progress.": "event.phaseChangeInProgress",
	"Please close any other open tabs.": "event.closeOtherTabs",
	"You can't auto play while you're fired!": "event.cantAutoPlayFired",
	"Something bad happened. Please try restarting your browser.":
		"event.restartBrowser",
};

const translateDuration = (count: number, unit: string) => {
	if (unit === "game" || unit === "games") {
		return i18n.t(count === 1 ? "event.durationGame" : "event.durationGames", {
			ns: "ui",
			count,
			defaultValue: `${count} ${count === 1 ? "game" : "games"}`,
		});
	}

	return i18n.t(count === 1 ? "event.durationWeek" : "event.durationWeeks", {
		ns: "ui",
		count,
		defaultValue: `${count} ${count === 1 ? "week" : "weeks"}`,
	});
};

const translateTeamLink = (teamLink: string) =>
	teamLink.replace(/>([^<]+)</, (_match, teamName: string) => {
		return `>${translateTeamName(teamName)}<`;
	});

// Team-operation events (relocation / rename / logo / expansion / contraction)
// anchor the *full* "region name" string (e.g. "Portland Trail Blazers"), not
// just the nickname, so localize the whole inner text via translateTeamFullName.
const translateTeamFullLink = (teamLink: string) =>
	teamLink.replace(
		/>([^<]+)<\/a>/,
		(_match, fullName: string) => `>${translateTeamFullName(fullName)}</a>`,
	);

const replaceAnchorText = (anchorHtml: string, text: string) =>
	anchorHtml.replace(/>[^<]*<\/a>$/, `>${text}</a>`);

const FEAT_STAT_KEYS: Record<string, string> = {
	point: "points",
	points: "points",
	rebound: "rebounds",
	rebounds: "rebounds",
	assist: "assists",
	assists: "assists",
	steal: "steals",
	steals: "steals",
	block: "blocks",
	blocks: "blocks",
	three: "threes",
	threes: "threes",
	"three pointer": "threes",
	"three pointers": "threes",
};

const CLUTCH_SHOT_KEYS: Record<string, string> = {
	"game-winning basket": "eventClutchShot.gameWinningBasket",
	"game-winning three-pointer": "eventClutchShot.gameWinningThreePointer",
	"game-winning free throw": "eventClutchShot.gameWinningFreeThrow",
	"game-winning three-point play": "eventClutchShot.gameWinningThreePointPlay",
	"game-winning four-point play": "eventClutchShot.gameWinningFourPointPlay",
	"game-tying basket": "eventClutchShot.gameTyingBasket",
	"game-tying three-pointer": "eventClutchShot.gameTyingThreePointer",
	"game-tying free throw": "eventClutchShot.gameTyingFreeThrow",
	"game-tying three-point play": "eventClutchShot.gameTyingThreePointPlay",
	"game-tying four-point play": "eventClutchShot.gameTyingFourPointPlay",
};

const translateFeatStatLine = (text: string) => {
	if (i18n.language !== "zh") {
		return text;
	}

	const normalized = text.replace(/ and /g, ", ");
	return normalized
		.split(", ")
		.map((part) => {
			const match = part.match(
				/^(an?|[0-9]+) (point|points|rebound|rebounds|assist|assists|steal|steals|block|blocks|three|threes|three pointer|three pointers)$/,
			);
			if (!match) {
				return part;
			}

			const countText =
				match[1] === "a" || match[1] === "an" ? "1" : match[1]!;
			const statKey = FEAT_STAT_KEYS[match[2]!] ?? match[2]!;
			return i18n.t(`eventFeatStat.${statKey}`, {
				ns: "ui",
				countText,
				defaultValue: `${countText} ${match[2]}`,
			});
		})
		.join("、");
};

const translateAssetList = (text: string) => {
	if (i18n.language !== "zh") {
		return text;
	}

	return text
		.replace(/\bnothing\b/g, "无")
		.replace(/\b(\d{4}) 1st round pick\b/g, "$1 年首轮签")
		.replace(/\b(\d{4}) 2nd round pick\b/g, "$1 年次轮签")
		.replace(/, and /g, "、")
		.replace(/ and /g, " 和 ")
		.replace(/, /g, "、");
};

const translateDetailsLink = (detailsLink: string) => {
	const detailsText = i18n.t("common.details", {
		ns: "ui",
		defaultValue: "Details",
	});
	if (i18n.language === "zh") {
		return detailsLink.replace(/>\(Details\)<\/a>$/, `>（${detailsText}）</a>`);
	}
	return detailsLink.replace(/>\(Details\)<\/a>$/, `>(${detailsText})</a>`);
};

const escapeRegExp = (text: string) =>
	text.replaceAll(/[.*+?^${}()|[\]\\]/g, "\\$&");

const LEGACY_TRAGIC_DEATH_TOKEN_TO_REGEX = {
	PLAYER_NAME: String.raw`(?<playerHtml><a [^>]+>.*?<\/a>)`,
	PRONOUN_He: String.raw`(?:He|She)`,
	PRONOUN_he: String.raw`(?:he|she)`,
	PRONOUN_him: String.raw`(?:him|her)`,
	PRONOUN_his: String.raw`(?:his|her)`,
	PRONOUN_himself: String.raw`(?:himself|herself)`,
} as const;

const buildLegacyTragicDeathRegex = (reason: string) => {
	let pattern = escapeRegExp(reason);

	for (const [token, replacement] of Object.entries(
		LEGACY_TRAGIC_DEATH_TOKEN_TO_REGEX,
	)) {
		pattern = pattern.replaceAll(escapeRegExp(token), replacement);
	}

	return new RegExp(`^${pattern}$`);
};

const LEGACY_TRAGIC_DEATH_MATCHERS = Object.entries(
	DEFAULT_TRAGIC_DEATH_REASON_TO_KEY,
).map(([reason, textKey]) => ({
	regex: buildLegacyTragicDeathRegex(reason),
	textKey,
}));

const translateTragicDeathParams = (
	textKey: string,
	textParams: Record<string, number | string>,
) => {
	const translatedParams = { ...textParams };
	const gender = translatedParams.gender;

	if (gender === "male" || gender === "female") {
		for (const pronoun of [
			"he",
			"He",
			"him",
			"Him",
			"his",
			"His",
			"himself",
			"Himself",
		] as const) {
			translatedParams[pronoun] = pronounLocalized(gender, pronoun);
		}
	}

	delete translatedParams.gender;

	if (textKey === "tragicDeath.clue") {
		for (const key of ["suspect", "room", "weapon"] as const) {
			const value = translatedParams[key];
			if (typeof value === "string") {
				translatedParams[key] = i18n.t(`tragicDeathParam.${key}.${value}`, {
					ns: "ui",
					defaultValue: value,
				});
			}
		}
	}

	if (textKey === "tragicDeath.gifts") {
		for (const key of ["gift1", "gift2"] as const) {
			const value = translatedParams[key];
			if (typeof value === "string") {
				translatedParams[key] = i18n.t(`tragicDeathParam.gift.${value}`, {
					ns: "ui",
					defaultValue: value,
				});
			}
		}
	}

	return translatedParams;
};

const getLegacyTragicDeathGender = (
	text: string,
): "female" | "male" | undefined => {
	if (/\b(?:She|she|her|herself)\b/.test(text)) {
		return "female";
	}
	if (/\b(?:He|he|his|him|himself)\b/.test(text)) {
		return "male";
	}
	return undefined;
};

const translateLegacyTragicDeath = (text: string) => {
	if (i18n.language !== "zh") {
		return undefined;
	}

	for (const { regex, textKey } of LEGACY_TRAGIC_DEATH_MATCHERS) {
		const match = text.match(regex);
		if (!match?.groups?.playerHtml) {
			continue;
		}

		const textParams: Record<string, string> = {
			playerHtml: match.groups.playerHtml,
		};
		const gender = getLegacyTragicDeathGender(text);
		if (gender) {
			textParams.gender = gender;
		}
		if (textKey === "tragicDeath.phoneBus") {
			textParams.gameName = GAME_NAME;
		}

		return i18n.t(textKey, {
			ns: "ui",
			defaultValue: text,
			...translateTragicDeathParams(textKey, textParams),
		});
	}

	const clueMatch = text.match(
		/^(?<playerHtml><a [^>]+>.*?<\/a>) was killed by (?<suspect>[^,]+), in the (?<room>[^,]+), with the (?<weapon>[^.]+)\.$/,
	);
	if (clueMatch?.groups?.playerHtml) {
		const textKey = "tragicDeath.clue";
		return i18n.t(textKey, {
			ns: "ui",
			defaultValue: text,
			...translateTragicDeathParams(textKey, {
				playerHtml: clueMatch.groups.playerHtml,
				room: clueMatch.groups.room!,
				suspect: clueMatch.groups.suspect!,
				weapon: clueMatch.groups.weapon!,
			}),
		});
	}

	const giftsMatch = text.match(
		/^(?<playerHtml><a [^>]+>.*?<\/a>) was smothered to death by gifts of (?<gift1>.+?) and (?<gift2>.+?) showered upon (?<him>him|her) by appreciative fans\.$/,
	);
	if (giftsMatch?.groups?.playerHtml) {
		const textKey = "tragicDeath.gifts";
		const gender = giftsMatch.groups.him === "her" ? "female" : "male";
		return i18n.t(textKey, {
			ns: "ui",
			defaultValue: text,
			...translateTragicDeathParams(textKey, {
				gender,
				gift1: giftsMatch.groups.gift1!,
				gift2: giftsMatch.groups.gift2!,
				him: giftsMatch.groups.him!,
				playerHtml: giftsMatch.groups.playerHtml,
			}),
		});
	}

	return undefined;
};

// --- A1: awards / finance / milestone / draft / playoff / release events ---
// server/worker emits these as plain English HTML without a textKey, so they
// are translated here (UI side) where team-name localization helpers live.

const AWARD_TEAM_NAME_TO_KEY: Record<string, string> = {
	"First Team All-League": "eventAwardTeam.firstAllLeague",
	"Second Team All-League": "eventAwardTeam.secondAllLeague",
	"Third Team All-League": "eventAwardTeam.thirdAllLeague",
	"First Team All-Defensive": "eventAwardTeam.firstAllDefensive",
	"Second Team All-Defensive": "eventAwardTeam.secondAllDefensive",
	"Third Team All-Defensive": "eventAwardTeam.thirdAllDefensive",
	"All-Rookie Team": "eventAwardTeam.allRookie",
};

const AWARD_LEADER_STAT_TO_KEY: Record<string, string> = {
	scoring: "eventAwardLeaderStat.scoring",
	rebounding: "eventAwardLeaderStat.rebounding",
	assists: "eventAwardLeaderStat.assists",
	steals: "eventAwardLeaderStat.steals",
	blocks: "eventAwardLeaderStat.blocks",
};

const PLAYOFF_LABEL_TO_KEY: Record<string, string> = {
	playoffs: "eventPlayoffLabel.playoffs",
	"play-in tournament": "eventPlayoffLabel.playIn",
};

const PLAYOFF_ROUND_TO_KEY: Record<string, string> = {
	"play-in tournament": "eventPlayoffRound.playIn",
	"1st round": "eventPlayoffRound.firstRound",
	quarterfinals: "eventPlayoffRound.quarterfinals",
	semifinals: "eventPlayoffRound.semifinals",
	"conference semifinals": "eventPlayoffRound.confSemifinals",
	"conference finals": "eventPlayoffRound.confFinals",
	finals: "eventPlayoffRound.finals",
};

// Round names come from server/worker/util/helpers.ts playoffRoundName. The
// fixed names map to keys; the dynamic "Nth round" form is zh-gated.
const translatePlayoffRoundName = (round: string) => {
	const key = PLAYOFF_ROUND_TO_KEY[round];
	if (key) {
		return i18n.t(key, { ns: "ui", defaultValue: round });
	}
	if (i18n.language === "zh") {
		const nthMatch = round.match(/^(\d+)(?:st|nd|rd|th) round$/);
		if (nthMatch) {
			return `第 ${nthMatch[1]} 轮`;
		}
	}
	return round;
};

// Series-lead tail of writeGameStats playoff game recaps (", taking a 2-1
// series lead" etc.). Returns "" for no tail, undefined for an unknown tail so
// the caller can fall back to the untouched English line.
const SERIES_STATUS_TAILS: {
	regex: RegExp;
	key: string;
}[] = [
	{
		regex: /^evening the series at (\d+)-(\d+)$/,
		key: "eventSeriesStatus.evening",
	},
	{
		regex: /^winning the series (\d+)-(\d+)$/,
		key: "eventSeriesStatus.winning",
	},
	{
		regex: /^taking a (\d+)-(\d+) series lead$/,
		key: "eventSeriesStatus.takingLead",
	},
	{
		regex: /^extending their (\d+)-(\d+) series lead$/,
		key: "eventSeriesStatus.extendingLead",
	},
	{
		regex: /^closing their (\d+)-(\d+) series deficit$/,
		key: "eventSeriesStatus.closingDeficit",
	},
];

const translateSeriesStatusTail = (tail: string | undefined) => {
	if (tail === undefined) {
		return "";
	}
	for (const { regex, key } of SERIES_STATUS_TAILS) {
		const match = tail.match(regex);
		if (match) {
			return i18n.t(key, {
				ns: "ui",
				won: match[1]!,
				lost: match[2]!,
				defaultValue: `, ${tail}`,
			});
		}
	}
	return undefined;
};

// playerFeat clutch lines can carry a playoff tail after the opponent name
// (writeGameStats endPart): " in the play-in tournament", " during game 3 of
// the conference finals", ", winning the 1st round 4-2", ", losing the finals".
// Split it off so the opponent gets translated and the tail localized.
const parseClutchOpponentTail = (raw: string) => {
	const duringMatch = raw.match(/^(.+?) during game (\d+) of the ([^,]+)$/);
	if (duringMatch) {
		return {
			opponent: duringMatch[1]!,
			roundContext: i18n.t("eventPlayoffTail.duringGame", {
				ns: "ui",
				gameNum: duringMatch[2]!,
				round: translatePlayoffRoundName(duringMatch[3]!),
				defaultValue: ` during game ${duringMatch[2]} of the ${duringMatch[3]}`,
			}),
			seriesOutcome: "",
		};
	}

	const inRoundMatch = raw.match(/^(.+?) in the ([^,]+)$/);
	if (inRoundMatch) {
		return {
			opponent: inRoundMatch[1]!,
			roundContext: i18n.t("eventPlayoffTail.inRound", {
				ns: "ui",
				round: translatePlayoffRoundName(inRoundMatch[2]!),
				defaultValue: ` in the ${inRoundMatch[2]}`,
			}),
			seriesOutcome: "",
		};
	}

	const winLoseMatch = raw.match(
		/^(.+?), (winning|losing) the (.+?)(?: (\d+)-(\d+))?$/,
	);
	if (winLoseMatch) {
		const winning = winLoseMatch[2] === "winning";
		const round = translatePlayoffRoundName(winLoseMatch[3]!);
		const seriesOutcome =
			winLoseMatch[4] !== undefined
				? i18n.t(
						winning
							? "eventPlayoffTail.winningRound"
							: "eventPlayoffTail.losingRound",
						{
							ns: "ui",
							round,
							won: winLoseMatch[4]!,
							lost: winLoseMatch[5]!,
							defaultValue: `, ${winLoseMatch[2]} the ${winLoseMatch[3]} ${winLoseMatch[4]}-${winLoseMatch[5]}`,
						},
					)
				: i18n.t(
						winning
							? "eventPlayoffTail.winningRoundNoScore"
							: "eventPlayoffTail.losingRoundNoScore",
						{
							ns: "ui",
							round,
							defaultValue: `, ${winLoseMatch[2]} the ${winLoseMatch[3]}`,
						},
					);
		return {
			opponent: winLoseMatch[1]!,
			roundContext: "",
			seriesOutcome,
		};
	}

	return { opponent: raw, roundContext: "", seriesOutcome: "" };
};

const FORCE_OT_SHOT_TO_KEY: Record<string, string> = {
	"a basket": "eventShot.basket",
	"a three-pointer": "eventShot.threePointer",
	"a free throw": "eventShot.freeThrow",
	"two free throws": "eventShot.twoFreeThrows",
	"three free throws": "eventShot.threeFreeThrows",
	"a three-point play": "eventShot.threePointPlay",
	"a four-point play": "eventShot.fourPointPlay",
};

const OT_COUNTER_TO_ZH: Record<string, string> = {
	"": "加时",
	double: "第二个加时",
	triple: "第三个加时",
	quadruple: "第四个加时",
	quintuple: "第五个加时",
	sextuple: "第六个加时",
	septuple: "第七个加时",
	octuple: "第八个加时",
};

// "to force ${counter} overtime" counter from server helpers.overtimeCounter:
// "", double..octuple, or "a Nth".
const translateOvertimeText = (counter: string) => {
	if (i18n.language === "zh") {
		const zhText = OT_COUNTER_TO_ZH[counter];
		if (zhText) {
			return zhText;
		}
		const nthMatch = counter.match(/^a (\d+)(?:st|nd|rd|th)$/);
		if (nthMatch) {
			return `第 ${nthMatch[1]} 个加时`;
		}
	}
	return counter === "" ? "overtime" : `${counter} overtime`;
};

const translateClutchTiming = (timingText: string, seconds?: string) => {
	if (timingText.startsWith("with ") && seconds !== undefined) {
		return i18n.t("event.playerFeatGameWinnerTimingSeconds", {
			ns: "ui",
			seconds,
			defaultValue: `with ${seconds} seconds remaining`,
		});
	}
	if (timingText === "at the buzzer") {
		return i18n.t("event.playerFeatGameWinnerTimingBuzzer", {
			ns: "ui",
			defaultValue: "at the buzzer",
		});
	}
	return i18n.t("event.playerFeatGameWinnerTimingNoTime", {
		ns: "ui",
		defaultValue: "with no time on the clock",
	});
};

const translateAwardTeamName = (name: string) => {
	const key = AWARD_TEAM_NAME_TO_KEY[name];
	return key ? i18n.t(key, { ns: "ui", defaultValue: name }) : name;
};

const translateAwardLeaderStat = (stat: string) => {
	const key = AWARD_LEADER_STAT_TO_KEY[stat];
	return key ? i18n.t(key, { ns: "ui", defaultValue: stat }) : stat;
};

// draftName is `${season} draft` (wrapped in an anchor), `${season} fantasy
// draft`, or `${season} expansion draft` (see draft/selectPlayer.ts). Only the
// draft-type words are localized; the year and any anchor are preserved.
const translateDraftName = (draftName: string) => {
	if (i18n.language !== "zh") {
		return draftName;
	}
	return draftName
		.replace(/(\d{4}) fantasy draft/, "$1 幻想选秀")
		.replace(/(\d{4}) expansion draft/, "$1 扩张选秀")
		.replace(/(\d{4}) draft/, "$1 选秀");
};

const translatePlayoffLink = (playoffLink: string) => {
	const inner = playoffLink.match(/>([^<]*)<\/a>$/)?.[1];
	if (inner === undefined) {
		return playoffLink;
	}
	const key = PLAYOFF_LABEL_TO_KEY[inner];
	if (!key) {
		return playoffLink;
	}
	return replaceAnchorText(
		playoffLink,
		i18n.t(key, { ns: "ui", defaultValue: inner }),
	);
};

// Awards read `<player> (<teamAbbrev>) <predicate>.` (season/awards.ts). Returns
// undefined when the predicate is not a recognized award phrase so the line
// falls through to other matchers / the English fallback untouched.
const translateAwardEventLine = (text: string) => {
	const prefixMatch = text.match(
		/^(?<playerHtml><a [^>]+>.*?<\/a>) \((?<teamHtml><a [^>]+>[^<]*<\/a>)\) (?<predicate>.+)\.$/,
	);
	if (!prefixMatch?.groups) {
		return undefined;
	}
	const { playerHtml, teamHtml, predicate } = prefixMatch.groups as {
		playerHtml: string;
		teamHtml: string;
		predicate: string;
	};
	const base = { ns: "ui", playerHtml, teamHtml } as const;

	if (predicate === "made the All-Star team") {
		return i18n.t("event.awardAllStarTeam", {
			...base,
			defaultValue: "{{playerHtml}} ({{teamHtml}}) made the All-Star team.",
		});
	}

	if (predicate === "won the slam dunk contest") {
		return i18n.t("event.awardSlamDunk", {
			...base,
			defaultValue: "{{playerHtml}} ({{teamHtml}}) won the slam dunk contest.",
		});
	}

	if (predicate === "won the three-point contest") {
		return i18n.t("event.awardThreePoint", {
			...base,
			defaultValue:
				"{{playerHtml}} ({{teamHtml}}) won the three-point contest.",
		});
	}

	const leaderMatch = predicate.match(/^led the league in (.+)$/);
	if (leaderMatch) {
		return i18n.t("event.awardLeagueLeader", {
			...base,
			stat: translateAwardLeaderStat(leaderMatch[1]!),
			defaultValue: "{{playerHtml}} ({{teamHtml}}) led the league in {{stat}}.",
		});
	}

	const allStarMvpMatch = predicate.match(
		/^won the (<a [^>]+>All-Star MVP<\/a>) award$/,
	);
	if (allStarMvpMatch) {
		const mvpLink = replaceAnchorText(
			allStarMvpMatch[1]!,
			i18n.t("eventAwardLabel.allStarMvp", {
				ns: "ui",
				defaultValue: "All-Star MVP",
			}),
		);
		return i18n.t("event.awardAllStarMvp", {
			...base,
			mvpLink,
			defaultValue: "{{playerHtml}} ({{teamHtml}}) won the {{mvpLink}} award.",
		});
	}

	const madeTeamMatch = predicate.match(/^made the (.+)$/);
	if (madeTeamMatch && AWARD_TEAM_NAME_TO_KEY[madeTeamMatch[1]!]) {
		return i18n.t("event.awardMadeTeam", {
			...base,
			teamAward: translateAwardTeamName(madeTeamMatch[1]!),
			defaultValue: "{{playerHtml}} ({{teamHtml}}) made the {{teamAward}}.",
		});
	}

	const wonAwardMatch = predicate.match(/^won the (.+) award$/);
	if (wonAwardMatch) {
		return i18n.t("event.awardWon", {
			...base,
			award: translateAwardName(wonAwardMatch[1]!),
			defaultValue: "{{playerHtml}} ({{teamHtml}}) won the {{award}} award.",
		});
	}

	return undefined;
};

const translateLegacyEventLine = (text: string) => {
	const tragicDeathText = translateLegacyTragicDeath(text);
	if (tragicDeathText) {
		return tragicDeathText;
	}

	const clinchedMatch = text.match(
		/^The (<a [^>]+>[^<]+<\/a>) (clinched a play-in tournament spot|clinched a playoffs spot|clinched a first round bye|clinched (?:a|the) #1 seed|have been eliminated from playoff contention)\.$/,
	);
	if (clinchedMatch) {
		const teamLink = translateTeamLink(clinchedMatch[1]!);
		const action = clinchedMatch[2]!;
		const key =
			action === "clinched a play-in tournament spot"
				? "event.teamClinchedPlayIn"
				: action === "clinched a playoffs spot"
					? "event.teamClinchedPlayoffs"
					: action === "clinched a first round bye"
						? "event.teamClinchedBye"
						: action.includes("#1 seed")
							? "event.teamClinchedTopSeed"
							: "event.teamEliminatedPlayoffs";

		return i18n.t(key, {
			ns: "ui",
			teamLink,
			defaultValue: text,
		});
	}

	const recoveredMatch = text.match(
		/^(.*?<\/a>) has recovered from (?:his|her) injury\.$/,
	);
	if (recoveredMatch) {
		return i18n.t("event.playerRecovered", {
			ns: "ui",
			playerHtml: recoveredMatch[1]!,
			defaultValue: text,
		});
	}

	const injuryListMatch = text.match(
		/^(.*?<\/a>) - ([^,]+), (\d+) (game|games|week|weeks)$/,
	);
	if (injuryListMatch) {
		const count = Number.parseInt(injuryListMatch[3]!, 10);
		return i18n.t("event.injuryListItem", {
			ns: "ui",
			playerHtml: injuryListMatch[1]!,
			injury: translateInjuryName(injuryListMatch[2]!),
			duration: translateDuration(count, injuryListMatch[4]!),
			defaultValue: text,
		});
	}

	const injuredMatch = text.match(
		/^(.*?<\/a>) (was injured|reaggravated (?:his|her) injury)! \(([^,]+), out for (\d+) (game|games|week|weeks)\)$/,
	);
	if (injuredMatch) {
		const count = Number.parseInt(injuredMatch[4]!, 10);
		return i18n.t(
			injuredMatch[2] === "was injured"
				? "event.playerInjured"
				: "event.playerReaggravatedInjury",
			{
				ns: "ui",
				playerHtml: injuredMatch[1]!,
				injury: translateInjuryName(injuredMatch[3]!),
				duration: translateDuration(count, injuredMatch[5]!),
				defaultValue: text,
			},
		);
	}

	// All-Star Game feat: checkStatisticalFeat / writeGameStats emit "win"/"loss"/
	// "tie in the All-Star Game" instead of the regular "win over the {opponent}"
	// tail, so these need dedicated matchers ahead of the generic feat/clutch ones.
	const ALL_STAR_RESULT_KEYS: Record<string, string> = {
		win: "event.playerFeatAllStarResultWin",
		loss: "event.playerFeatAllStarResultLoss",
		tie: "event.playerFeatAllStarResultTie",
	};

	const allStarFeatMatch = text.match(
		/^(<a [^>]+>.*?<\/a>) had (<a [^>]+>(.*?)<\/a>) in (?:a|an) (.+?) (win|loss|tie) in the All-Star Game\.$/,
	);
	if (allStarFeatMatch) {
		return i18n.t("event.playerFeatAllStar", {
			ns: "ui",
			playerHtml: allStarFeatMatch[1]!,
			statsHtml: replaceAnchorText(
				allStarFeatMatch[2]!,
				translateFeatStatLine(allStarFeatMatch[3]!),
			),
			score: allStarFeatMatch[4]!,
			result: i18n.t(ALL_STAR_RESULT_KEYS[allStarFeatMatch[5]!]!, {
				ns: "ui",
				defaultValue: allStarFeatMatch[5]!,
			}),
			defaultValue: text,
		});
	}

	const allStarClutchMatch = text.match(
		/^(<a [^>]+>.*?<\/a>) made a (game-winning|game-tying) (basket|three-pointer|free throw|three-point play|four-point play) (with ([0-9.]+) seconds remaining|at the buzzer|with no time on the clock) in (?:a|an) (<a [^>]+>[^<]*<\/a>) (win|loss|tie) in the All-Star Game\.$/,
	);
	if (allStarClutchMatch) {
		const shotKey =
			CLUTCH_SHOT_KEYS[
				`${allStarClutchMatch[2]!} ${allStarClutchMatch[3]!}`
			] ?? undefined;
		const shotText = shotKey
			? i18n.t(shotKey, {
					ns: "ui",
					defaultValue: `made a ${allStarClutchMatch[2]} ${allStarClutchMatch[3]}`,
				})
			: `made a ${allStarClutchMatch[2]} ${allStarClutchMatch[3]}`;
		return i18n.t("event.playerFeatAllStarClutch", {
			ns: "ui",
			playerHtml: allStarClutchMatch[1]!,
			shotText,
			timing: translateClutchTiming(
				allStarClutchMatch[4]!,
				allStarClutchMatch[5],
			),
			scoreLink: allStarClutchMatch[6]!,
			result: i18n.t(ALL_STAR_RESULT_KEYS[allStarClutchMatch[7]!]!, {
				ns: "ui",
				defaultValue: allStarClutchMatch[7]!,
			}),
			defaultValue: text,
		});
	}

	const featMatch = text.match(
		/^(<a [^>]+>.*?<\/a>) had (<a [^>]+>(.*?)<\/a>) in (?:a|an) (.+?) (win over the|loss to the|tie with the) ([^.]+)\.$/,
	);
	if (featMatch) {
		const resultVerbKey =
			featMatch[5] === "win over the"
				? "event.playerFeatResultWin"
				: featMatch[5] === "loss to the"
					? "event.playerFeatResultLoss"
					: "event.playerFeatResultTie";
		return i18n.t("event.playerFeat", {
			ns: "ui",
			playerHtml: featMatch[1]!,
			statsHtml: replaceAnchorText(
				featMatch[2]!,
				translateFeatStatLine(featMatch[3]!),
			),
			score: featMatch[4]!,
			resultVerb: i18n.t(resultVerbKey, {
				ns: "ui",
				defaultValue: featMatch[5]!,
			}),
			opponent: translateTeamName(featMatch[6]!),
			defaultValue: text,
		});
	}

	const clutchShotMatch = text.match(
		/^(<a [^>]+>.*?<\/a>) made a (game-winning|game-tying) (basket|three-pointer|free throw|three-point play|four-point play) (with ([0-9.]+) seconds remaining|at the buzzer|with no time on the clock) in (?:a|an) (.+?) (win over|loss to|tie with) the ([^.]+)\.$/,
	);
	if (clutchShotMatch) {
		const timing = translateClutchTiming(
			clutchShotMatch[4]!,
			clutchShotMatch[5],
		);
		const resultVerbKey =
			clutchShotMatch[7] === "win over"
				? "event.playerFeatResultWin"
				: clutchShotMatch[7] === "loss to"
					? "event.playerFeatResultLoss"
					: "event.playerFeatResultTie";
		const shotKey =
			CLUTCH_SHOT_KEYS[
				`${clutchShotMatch[2]!} ${clutchShotMatch[3]!}`
			] ?? undefined;
		const shotText = shotKey
			? i18n.t(shotKey, {
					ns: "ui",
					defaultValue: `made a ${clutchShotMatch[2]} ${clutchShotMatch[3]}`,
				})
			: `made a ${clutchShotMatch[2]} ${clutchShotMatch[3]}`;
		const { opponent, roundContext, seriesOutcome } = parseClutchOpponentTail(
			clutchShotMatch[8]!,
		);

		return i18n.t("event.playerFeatClutchShot", {
			ns: "ui",
			playerHtml: clutchShotMatch[1]!,
			timing,
			shotText,
			score: clutchShotMatch[6]!,
			resultVerb: i18n.t(resultVerbKey, {
				ns: "ui",
				defaultValue: clutchShotMatch[7]!,
			}),
			opponent: translateTeamName(opponent),
			roundContext,
			seriesOutcome,
			defaultValue: text,
		});
	}

	// Short clutch line attached to box scores (writeGameStats pushes the
	// GameSim clutch text with no game-result tail).
	const clutchShotShortMatch = text.match(
		/^(<a [^>]+>.*?<\/a>) made a (game-winning|game-tying) (basket|three-pointer|free throw|three-point play|four-point play) (with ([0-9.]+) seconds remaining|at the buzzer|with no time on the clock)\.$/,
	);
	if (clutchShotShortMatch) {
		const shotKey =
			CLUTCH_SHOT_KEYS[
				`${clutchShotShortMatch[2]!} ${clutchShotShortMatch[3]!}`
			] ?? undefined;
		const shotText = shotKey
			? i18n.t(shotKey, {
					ns: "ui",
					defaultValue: `made a ${clutchShotShortMatch[2]} ${clutchShotShortMatch[3]}`,
				})
			: `made a ${clutchShotShortMatch[2]} ${clutchShotShortMatch[3]}`;
		return i18n.t("event.playerFeatClutchShotShort", {
			ns: "ui",
			playerHtml: clutchShotShortMatch[1]!,
			shotText,
			timing: translateClutchTiming(
				clutchShotShortMatch[4]!,
				clutchShotShortMatch[5],
			),
			defaultValue: text,
		});
	}

	// GameSim "forced overtime" clutch line, either the persisted event form
	// (with score / opponent / optional playoff tail) or the short box-score
	// form. The double space in "to force  overtime" comes from the server's
	// empty overtimeCounter("").
	const forceOtMatch = text.match(
		/^(<a [^>]+>.*?<\/a>) made (a basket|a three-pointer|a free throw|two free throws|three free throws|a three-point play|a four-point play) (with ([0-9.]+) seconds remaining|at the buzzer|with no time on the clock) to force (|double|triple|quadruple|quintuple|sextuple|septuple|octuple|a \d+(?:st|nd|rd|th)) overtime(?: in (?:a|an) (<a [^>]+>[^<]*<\/a>) (win over|loss to|tie with) the (.+?))?\.$/,
	);
	if (forceOtMatch) {
		const shotKey = FORCE_OT_SHOT_TO_KEY[forceOtMatch[2]!];
		const shotText = shotKey
			? i18n.t(shotKey, { ns: "ui", defaultValue: forceOtMatch[2]! })
			: forceOtMatch[2]!;
		const timing = translateClutchTiming(forceOtMatch[3]!, forceOtMatch[4]);
		const otText = translateOvertimeText(forceOtMatch[5]!);

		if (forceOtMatch[6] === undefined) {
			return i18n.t("event.playerFeatForceOvertimeShort", {
				ns: "ui",
				playerHtml: forceOtMatch[1]!,
				shotText,
				timing,
				otText,
				defaultValue: text,
			});
		}

		const resultVerbKey =
			forceOtMatch[7] === "win over"
				? "event.playerFeatResultWin"
				: forceOtMatch[7] === "loss to"
					? "event.playerFeatResultLoss"
					: "event.playerFeatResultTie";
		const { opponent, roundContext, seriesOutcome } = parseClutchOpponentTail(
			forceOtMatch[8]!,
		);

		return i18n.t("event.playerFeatForceOvertime", {
			ns: "ui",
			playerHtml: forceOtMatch[1]!,
			shotText,
			timing,
			otText,
			scoreLink: forceOtMatch[6]!,
			resultVerb: i18n.t(resultVerbKey, {
				ns: "ui",
				defaultValue: forceOtMatch[7]!,
			}),
			opponent: translateTeamName(opponent),
			roundContext,
			seriesOutcome,
			defaultValue: text,
		});
	}

	const tradeMatch = text.match(
		/^The (<a [^>]+>[^<]+<\/a>) traded (.+) to the (<a [^>]+>[^<]+<\/a>) for (.+)\. (<a [^>]+>\(Details\)<\/a>)$/,
	);
	if (tradeMatch) {
		return i18n.t("event.tradeSummary", {
			ns: "ui",
			fromTeam: translateTeamLink(tradeMatch[1]!),
			assetsFrom: translateAssetList(tradeMatch[2]!),
			toTeam: translateTeamLink(tradeMatch[3]!),
			assetsTo: translateAssetList(tradeMatch[4]!),
			detailsLink: translateDetailsLink(tradeMatch[5]!),
			defaultValue: text,
		});
	}

	const signingMatch = text.match(
		/^The (<a [^>]+>[^<]+<\/a>) (signed|re-signed) (<a [^>]+>.*?<\/a>) for (.+)\/year through (\d+)\.$/,
	);
	if (signingMatch) {
		return i18n.t(
			signingMatch[2] === "signed"
				? "event.freeAgentSigning"
				: "event.reSignedPlayer",
			{
				ns: "ui",
				teamLink: translateTeamLink(signingMatch[1]!),
				playerLink: signingMatch[3]!,
				amount: signingMatch[4]!,
				exp: signingMatch[5]!,
				defaultValue: text,
			},
		);
	}

	const wolvesMatch = text.match(
		/^(<a [^>]+>.*?<\/a>) was eaten by wolves\. (He|She) was delicious\.$/,
	);
	if (wolvesMatch) {
		return i18n.t("event.tragedyEatenByWolves", {
			ns: "ui",
			playerHtml: wolvesMatch[1]!,
			He: wolvesMatch[2]!,
			defaultValue: text,
		});
	}

	const awardText = translateAwardEventLine(text);
	if (awardText !== undefined) {
		return awardText;
	}

	const minPayrollMatch = text.match(
		/^The (<a [^>]+>[^<]*<\/a>) paid a minimum payroll penalty of (.+?) for having a payroll under (.+?)\.$/,
	);
	if (minPayrollMatch) {
		return i18n.t("event.financeMinPayroll", {
			ns: "ui",
			teamLink: translateTeamLink(minPayrollMatch[1]!),
			amount: minPayrollMatch[2]!,
			threshold: minPayrollMatch[3]!,
			defaultValue:
				"The {{teamLink}} paid a minimum payroll penalty of {{amount}} for having a payroll under {{threshold}}.",
		});
	}

	const luxuryTaxMatch = text.match(
		/^The (<a [^>]+>[^<]*<\/a>) paid a luxury tax of (.+?) for having a payroll above (.+?)\.$/,
	);
	if (luxuryTaxMatch) {
		return i18n.t("event.financeLuxuryTax", {
			ns: "ui",
			teamLink: translateTeamLink(luxuryTaxMatch[1]!),
			amount: luxuryTaxMatch[2]!,
			threshold: luxuryTaxMatch[3]!,
			defaultValue:
				"The {{teamLink}} paid a luxury tax of {{amount}} for having a payroll above {{threshold}}.",
		});
	}

	const luxuryTaxDistMatch = text.match(
		/^The (<a [^>]+>[^<]*<\/a>) received a luxury tax distribution of (.+?) for having a payroll under (.+?)\.$/,
	);
	if (luxuryTaxDistMatch) {
		return i18n.t("event.financeLuxuryTaxDist", {
			ns: "ui",
			teamLink: translateTeamLink(luxuryTaxDistMatch[1]!),
			amount: luxuryTaxDistMatch[2]!,
			threshold: luxuryTaxDistMatch[3]!,
			defaultValue:
				"The {{teamLink}} received a luxury tax distribution of {{amount}} for having a payroll under {{threshold}}.",
		});
	}

	const retiredMatch = text.match(/^(<a [^>]+>.*?<\/a>) retired\.$/);
	if (retiredMatch) {
		return i18n.t("event.playerRetired", {
			ns: "ui",
			playerHtml: retiredMatch[1]!,
			defaultValue: "{{playerHtml}} retired.",
		});
	}

	const hofMatch = text.match(
		/^(<a [^>]+>.*?<\/a>) was inducted into the Hall of Fame\.$/,
	);
	if (hofMatch) {
		return i18n.t("event.playerHallOfFame", {
			ns: "ui",
			playerHtml: hofMatch[1]!,
			defaultValue: "{{playerHtml}} was inducted into the Hall of Fame.",
		});
	}

	const retiredJerseyMatch = text.match(
		/^The (.+?) retired (<a [^>]+>.*?<\/a>)'s #(\d+)\.$/,
	);
	if (retiredJerseyMatch) {
		return i18n.t("event.retiredJersey", {
			ns: "ui",
			teamName: translateTeamFullName(retiredJerseyMatch[1]!),
			playerHtml: retiredJerseyMatch[2]!,
			number: retiredJerseyMatch[3]!,
			defaultValue: "The {{teamName}} retired {{playerHtml}}'s #{{number}}.",
		});
	}

	const draftMatch = text.match(
		/^The (<a [^>]+>[^<]*<\/a>) selected (<a [^>]+>.*?<\/a>) with the (\d+)(?:st|nd|rd|th) pick in the (.+)\.$/,
	);
	if (draftMatch) {
		return i18n.t("event.draftSelected", {
			ns: "ui",
			teamLink: translateTeamLink(draftMatch[1]!),
			playerHtml: draftMatch[2]!,
			pick: ordinalLocalized(Number.parseInt(draftMatch[3]!, 10)),
			draftName: translateDraftName(draftMatch[4]!),
			defaultValue:
				"The {{teamLink}} selected {{playerHtml}} with the {{pick}} pick in the {{draftName}}.",
		});
	}

	const lotteryChanceMatch = text.match(
		/^The (<a [^>]+>[^<]*<\/a>) have a ([\d.]+)% chance of getting the top overall pick of the (\d{4}) draft\.$/,
	);
	if (lotteryChanceMatch) {
		return i18n.t("event.lotteryChance", {
			ns: "ui",
			teamLink: translateTeamLink(lotteryChanceMatch[1]!),
			percent: lotteryChanceMatch[2]!,
			year: lotteryChanceMatch[3]!,
			defaultValue:
				"The {{teamLink}} have a {{percent}}% chance of getting the top overall pick of the {{year}} draft.",
		});
	}

	const lotteryMovedMatch = text.match(
		/^The (<a [^>]+>[^<]*<\/a>) moved (up|down) in the lottery and will select (\d+)(?:st|nd|rd|th) overall in the (\d{4}) draft\.$/,
	);
	if (lotteryMovedMatch) {
		return i18n.t(
			lotteryMovedMatch[2] === "up"
				? "event.lotteryMovedUp"
				: "event.lotteryMovedDown",
			{
				ns: "ui",
				teamLink: translateTeamLink(lotteryMovedMatch[1]!),
				pick: ordinalLocalized(Number.parseInt(lotteryMovedMatch[3]!, 10)),
				year: lotteryMovedMatch[4]!,
				defaultValue:
					lotteryMovedMatch[2] === "up"
						? "The {{teamLink}} moved up in the lottery and will select {{pick}} overall in the {{year}} draft."
						: "The {{teamLink}} moved down in the lottery and will select {{pick}} overall in the {{year}} draft.",
			},
		);
	}

	const lotteryNormalMatch = text.match(
		/^The (<a [^>]+>[^<]*<\/a>) will select (\d+)(?:st|nd|rd|th) overall in the (\d{4}) draft\.$/,
	);
	if (lotteryNormalMatch) {
		return i18n.t("event.lotteryNormal", {
			ns: "ui",
			teamLink: translateTeamLink(lotteryNormalMatch[1]!),
			pick: ordinalLocalized(Number.parseInt(lotteryNormalMatch[2]!, 10)),
			year: lotteryNormalMatch[3]!,
			defaultValue:
				"The {{teamLink}} will select {{pick}} overall in the {{year}} draft.",
		});
	}

	// Per-game playoff recap (writeGameStats): "The <a>W</a> defeated the
	// <a>L</a> <a>116-92</a> in game 1 of the conference finals, taking a 1-0
	// series lead." / best-of-1: "... <a>14-9</a> in the finals."
	const playoffGameRecapMatch = text.match(
		/^The (<a [^>]+>[^<]*<\/a>) defeated the (<a [^>]+>[^<]*<\/a>) (<a [^>]+>[^<]*<\/a>) in(?: game (\d+) of)? the ([^,.]+?)(?:, ([^.]+))?\.$/,
	);
	if (playoffGameRecapMatch) {
		const seriesStatus = translateSeriesStatusTail(playoffGameRecapMatch[6]);
		if (seriesStatus !== undefined) {
			const base = {
				ns: "ui",
				winnerLink: translateTeamLink(playoffGameRecapMatch[1]!),
				loserLink: translateTeamLink(playoffGameRecapMatch[2]!),
				scoreLink: playoffGameRecapMatch[3]!,
				round: translatePlayoffRoundName(playoffGameRecapMatch[5]!),
				defaultValue: text,
			} as const;
			if (playoffGameRecapMatch[4] !== undefined) {
				return i18n.t("event.playoffGameRecapSeries", {
					...base,
					gameNum: playoffGameRecapMatch[4]!,
					seriesStatus,
				});
			}
			return i18n.t("event.playoffGameRecap", base);
		}
	}

	// Series result (updatePlayoffSeries): "The <a>W</a> defeated the <a>L</a>
	// in the conference finals, 4-2." (score is series wins, or points for
	// best-of-1 rounds).
	const playoffSeriesResultMatch = text.match(
		/^The (<a [^>]+>[^<]*<\/a>) defeated the (<a [^>]+>[^<]*<\/a>) in the ([^,.]+), ([^.]+)\.$/,
	);
	if (playoffSeriesResultMatch) {
		return i18n.t("event.playoffSeriesResult", {
			ns: "ui",
			winnerLink: translateTeamLink(playoffSeriesResultMatch[1]!),
			loserLink: translateTeamLink(playoffSeriesResultMatch[2]!),
			round: translatePlayoffRoundName(playoffSeriesResultMatch[3]!),
			score: playoffSeriesResultMatch[4]!,
			defaultValue: text,
		});
	}

	const championsMatch = text.match(
		/^The (<a [^>]+>[^<]*<\/a>) finished in 1st place and are league champions!$/,
	);
	if (championsMatch) {
		return i18n.t("event.leagueChampions", {
			ns: "ui",
			teamLink: translateTeamLink(championsMatch[1]!),
			defaultValue:
				"The {{teamLink}} finished in 1st place and are league champions!",
		});
	}

	const madePlayoffsMatch = text.match(
		/^The (<a [^>]+>[^<]*<\/a>) made the (<a [^>]+>[^<]*<\/a>)\.$/,
	);
	if (madePlayoffsMatch) {
		return i18n.t("event.madePlayoffs", {
			ns: "ui",
			teamLink: translateTeamLink(madePlayoffsMatch[1]!),
			playoffLink: translatePlayoffLink(madePlayoffsMatch[2]!),
			defaultValue: "The {{teamLink}} made the {{playoffLink}}.",
		});
	}

	const releaseMatch = text.match(
		/^The (<a [^>]+>[^<]*<\/a>) released (<a [^>]+>.*?<\/a>)\.$/,
	);
	if (releaseMatch) {
		return i18n.t("event.playerReleased", {
			ns: "ui",
			teamLink: translateTeamLink(releaseMatch[1]!),
			playerHtml: releaseMatch[2]!,
			defaultValue: "The {{teamLink}} released {{playerHtml}}.",
		});
	}

	// --- Team operations: relocation / rename / logo / expansion / contraction ---
	// server/worker/util/processScheduledEvents.ts + core/team/{relocateVote,
	// expandVote,disable,addNewTeamToExistingLeague}.ts + core/expansionDraft.
	// All emit plain English with the full "region name" as the entity string.

	const relocateVoteWinMatch = text.match(
		/^The (.+?) are now the (<a [^>]+>[^<]+<\/a>) after a successful (\d+)-(\d+) vote\.$/,
	);
	if (relocateVoteWinMatch) {
		return i18n.t("event.teamRelocateVoteWin", {
			ns: "ui",
			oldTeam: translateTeamFullName(relocateVoteWinMatch[1]!),
			newLink: translateTeamFullLink(relocateVoteWinMatch[2]!),
			for: relocateVoteWinMatch[3]!,
			against: relocateVoteWinMatch[4]!,
			defaultValue:
				"The {{oldTeam}} are now the {{newLink}} after a successful {{for}}-{{against}} vote.",
		});
	}

	// Relocation and rename scheduled events share this text; the type differs but
	// the wording does not, so one neutral "are now the" line covers both.
	const teamRenamedMatch = text.match(
		/^The (.+?) are now the (<a [^>]+>[^<]+<\/a>)\.$/,
	);
	if (teamRenamedMatch) {
		return i18n.t("event.teamRenamed", {
			ns: "ui",
			oldTeam: translateTeamFullName(teamRenamedMatch[1]!),
			newLink: translateTeamFullLink(teamRenamedMatch[2]!),
			defaultValue: "The {{oldTeam}} are now the {{newLink}}.",
		});
	}

	const relocateVoteLoseMatch = text.match(
		/^The (<a [^>]+>[^<]+<\/a>) wanted to move to (.+?), but they lost the vote (\d+)-(\d+)\.$/,
	);
	if (relocateVoteLoseMatch) {
		return i18n.t("event.teamRelocateVoteLose", {
			ns: "ui",
			teamLink: translateTeamFullLink(relocateVoteLoseMatch[1]!),
			region: translateTeamRegion(relocateVoteLoseMatch[2]!),
			against: relocateVoteLoseMatch[3]!,
			for: relocateVoteLoseMatch[4]!,
			defaultValue:
				"The {{teamLink}} wanted to move to {{region}}, but they lost the vote {{against}}-{{for}}.",
		});
	}

	// The logo event embeds "<br><img ...>", so translateEventText has already
	// split it; this matches the first line only (the trailing image passes
	// through untouched and is re-joined by the caller).
	const teamLogoMatch = text.match(
		/^The (<a [^>]+>[^<]+<\/a>) got a new logo:$/,
	);
	if (teamLogoMatch) {
		return i18n.t("event.teamNewLogo", {
			ns: "ui",
			teamLink: translateTeamFullLink(teamLogoMatch[1]!),
			defaultValue: "The {{teamLink}} got a new logo:",
		});
	}

	const teamJoiningMatch = text.match(
		/^The (<a [^>]+>[^<]+<\/a>) are joining the league\.$/,
	);
	if (teamJoiningMatch) {
		return i18n.t("event.teamJoiningLeague", {
			ns: "ui",
			teamLink: translateTeamFullLink(teamJoiningMatch[1]!),
			defaultValue: "The {{teamLink}} are joining the league.",
		});
	}

	const expandVoteLoseMatch = text.match(
		/^(An expansion team|\d+ expansion teams) wanted to join the league, but they lost the vote (\d+)-(\d+)\.$/,
	);
	if (expandVoteLoseMatch) {
		const numMatch = expandVoteLoseMatch[1]!.match(/^(\d+) expansion teams$/);
		const subject = numMatch
			? i18n.t("event.expansionSubjectPlural", {
					ns: "ui",
					count: numMatch[1]!,
					defaultValue: `${numMatch[1]} expansion teams`,
				})
			: i18n.t("event.expansionSubjectSingular", {
					ns: "ui",
					defaultValue: "An expansion team",
				});
		return i18n.t("event.expansionVoteLose", {
			ns: "ui",
			subject,
			against: expandVoteLoseMatch[2]!,
			for: expandVoteLoseMatch[3]!,
			defaultValue:
				"{{subject}} wanted to join the league, but they lost the vote {{against}}-{{for}}.",
		});
	}

	const newTeamMatch = text.match(
		/^A new team called the (.+?) (will be created in an expansion draft|was created)\.$/,
	);
	if (newTeamMatch) {
		return i18n.t(
			newTeamMatch[2] === "was created"
				? "event.newTeamCreated"
				: "event.newTeamExpansion",
			{
				ns: "ui",
				teamName: translateTeamFullName(newTeamMatch[1]!),
				defaultValue:
					newTeamMatch[2] === "was created"
						? "A new team called the {{teamName}} was created."
						: "A new team called the {{teamName}} will be created in an expansion draft.",
			},
		);
	}

	const newGmMatch = text.match(
		/^You are now the GM of a new expansion team, the (.+?)!$/,
	);
	if (newGmMatch) {
		return i18n.t("event.newExpansionGm", {
			ns: "ui",
			teamName: translateTeamFullName(newGmMatch[1]!),
			defaultValue:
				"You are now the GM of a new expansion team, the {{teamName}}!",
		});
	}

	const contractionMatch = text.match(
		/^The (<a [^>]+>[^<]+<\/a>) are disbanding\. All their players will become free agents\.$/,
	);
	if (contractionMatch) {
		return i18n.t("event.teamContraction", {
			ns: "ui",
			teamLink: translateTeamFullLink(contractionMatch[1]!),
			defaultValue:
				"The {{teamLink}} are disbanding. All their players will become free agents.",
		});
	}

	// All-Star Game result (writeGameStats): "{team1} defeated {team2} <a>{score}
	// in the All-Star Game</a>." The team labels are dynamic (captain / conference
	// names) so they pass through; the verb + "All-Star Game" label are localized.
	const allStarGameMatch = text.match(
		/^(.+?) (defeated|tied) (.+?) (<a [^>]+>)(.+?) in the All-Star Game<\/a>\.$/,
	);
	if (allStarGameMatch) {
		const label = i18n.t("event.allStarGameLabel", {
			ns: "ui",
			defaultValue: "in the All-Star Game",
		});
		const scoreLink =
			i18n.language === "zh"
				? `${allStarGameMatch[4]!}${allStarGameMatch[5]!}（${label}）</a>`
				: `${allStarGameMatch[4]!}${allStarGameMatch[5]!} ${label}</a>`;
		return i18n.t(
			allStarGameMatch[2] === "tied"
				? "event.allStarGameResultTie"
				: "event.allStarGameResultWin",
			{
				ns: "ui",
				team1: allStarGameMatch[1]!,
				team2: allStarGameMatch[3]!,
				scoreLink,
				defaultValue:
					allStarGameMatch[2] === "tied"
						? "{{team1}} tied {{team2}} {{scoreLink}}."
						: "{{team1}} defeated {{team2}} {{scoreLink}}.",
			},
		);
	}

	return text;
};

export const translateEventText = (event: {
	text: string;
	textKey?: string;
	textParams?: Record<string, number | string>;
}) => {
	if (event.textKey) {
		const textParams =
			event.textParams && event.textKey.startsWith("tragicDeath.")
				? translateTragicDeathParams(event.textKey, event.textParams)
				: event.textParams;
		return i18n.t(event.textKey, {
			ns: "ui",
			defaultValue: event.text,
			...textParams,
		});
	}

	const legacyKey = LEGACY_TEXT_TO_KEY[event.text];
	if (legacyKey) {
		return i18n.t(legacyKey, { ns: "ui", defaultValue: event.text });
	}

	const lines = event.text.split("<br>");
	if (lines.length > 1) {
		return lines.map(translateLegacyEventLine).join("<br>");
	}

	return translateLegacyEventLine(event.text);
};
